import { type Theme } from '#app/theme/index.js';
import { type TextFieldVariant } from '../TextField.js';
import { StyledTextFieldInputGroup } from './StyledTextFieldInputGroup.js';
import { css, styled } from 'styled-components';

type StyledTextFieldProps = {
  $hasError: boolean;
  $hasFullyRoundedBorders: boolean;
  $isBorderless: boolean;
  $isDisabled: boolean;
  $isHighlighted: boolean;
  size: 'default' | 'small';
  variant: TextFieldVariant;
};

const getBorderColor = ({
  defaultGray,
  hasError,
  isDisabled,
  isHighlighted,
  theme,
}: {
  defaultGray?: boolean;
  hasError: boolean;
  isDisabled: boolean;
  isHighlighted: boolean;
  theme: Theme;
}) => {
  if (isDisabled) {
    return theme.colorsV2.gray20;
  }

  if (isHighlighted) {
    return theme.colorsV2.gray90;
  }

  if (hasError) {
    return theme.colorsV2.red50;
  }

  if (defaultGray) {
    return theme.colorsV2.gray40;
  }

  return theme.colorsV2.gray40;
};

export const StyledTextField = styled.div<StyledTextFieldProps>`
  ${({
    $hasError,
    $hasFullyRoundedBorders,
    $isBorderless,
    $isDisabled,
    $isHighlighted,
    size,
    theme,
    variant = 'default',
  }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    min-height: 56px;
    border-radius: 8px;
    border: 1px solid ${theme.colorsV2.gray30};
    border-color: ${getBorderColor({
      hasError: $hasError,
      isDisabled: $isDisabled,
      isHighlighted: $isHighlighted,
      theme,
    })};
    background-color: ${theme.colors.white20};
    text-align: left;
    transition: border-color ease-in-out 0.2s;

    ${$hasFullyRoundedBorders &&
    css`
      border-radius: 8px;
      border-color: ${getBorderColor({
        defaultGray: true,
        hasError: $hasError,
        isDisabled: $isDisabled,
        isHighlighted: $isHighlighted,
        theme,
      })};
    `}

    ${$isBorderless &&
    css`
      border-width: 0 0 1px 0;
      background: transparent;
    `}

    ${variant === 'uniformBorders' &&
    css`
      border-bottom-color: unset;
      border: none;
      padding: 10px 0px 8px 0px;
      border: 1px solid ${theme.colorsV2.gray30};
      margin-top: 12px;
      border-radius: 10px;
      max-width: 560px;

      &[aria-invalid='true'] {
        border: 1px solid ${theme.colorsV2.red50};
      }
    `}
      
    &:focus-within {
      border-color: ${$isDisabled || $isHighlighted || $hasError
        ? getBorderColor({
            hasError: $hasError,
            isDisabled: $isDisabled,
            isHighlighted: $isHighlighted,
            theme,
          })
        : theme.colorsV2.gray40};
    }

    &:hover {
      border-color: ${$isDisabled || $isHighlighted || $hasError
        ? getBorderColor({
            hasError: $hasError,
            isDisabled: $isDisabled,
            isHighlighted: $isHighlighted,
            theme,
          })
        : theme.colorsV2.gray50};

      &:focus-within {
        border-color: ${$isDisabled || $isHighlighted || $hasError
          ? getBorderColor({
              hasError: $hasError,
              isDisabled: $isDisabled,
              isHighlighted: $isHighlighted,
              theme,
            })
          : theme.colorsV2.gray40};
      }
    }

    ${variant === 'uniformBordersV2' &&
    css`
      border-bottom-color: unset;
      border: none;
      padding: 7px;
      border: 1px solid ${theme.colorsV2.gray40};
      border-radius: 8px;

      &[aria-invalid='true'] {
        border: 1px solid ${theme.colorsV2.red50};
      }

      transition: border-color 0.2s ease-out;

      &:hover {
        border-color: ${theme.colorsV2.gray90};

        &:focus-within {
          border-color: ${theme.colorsV2.gray90};
        }
      }

      &:focus-within {
        border-color: ${theme.colorsV2.gray90};
      }
    `}

    /* Remove vertical padding and min-height */
    ${size === 'small' &&
    css`
      min-height: 44px;
      padding: 0px;
      margin-top: 0px;

      ${StyledTextFieldInputGroup} {
        padding-bottom: 0px;
      }
    `}
  `}
`;
